import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import { ChangeEvent, FocusEvent, FormEvent, FunctionComponent, MouseEvent, useState } from 'react';
import { connect } from 'react-redux';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import { Button } from 'dibs-elements/exports/Button';
import { Input } from 'dibs-elements/exports/Input';

import { saveSidemark, updateSidemarkStatusAction } from '../../actions/checkoutUIActions';
import { VARS } from '../../constants/checkoutConstants';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { isOnReviewPane, isOnSidemarkPane, PaneState } from '../../reducers/checkoutPaneReducer';
import { CheckoutEditValue } from '../../components/shared/CheckoutEditValue';
import { copy } from '../../components/shared/copy';

import { CheckoutSidebarSidemarkForm_transaction$key } from './__generated__/CheckoutSidebarSidemarkForm_transaction.graphql';
import { CheckoutSidebarSidemarkForm_viewer$key } from './__generated__/CheckoutSidebarSidemarkForm_viewer.graphql';

import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import styles from './styles/CheckoutSidebarSidemarkForm.scss';

const messages = defineMessages({
    placeholder: {
        id: 'checkout.CheckoutSidebarSidemarkForm.placeholder',
        defaultMessage: 'Optional',
    },
});

type Props = {
    viewer: CheckoutSidebarSidemarkForm_viewer$key;
    transaction: CheckoutSidebarSidemarkForm_transaction$key;
    sidemark: string | null;
    hasUnsavedSidemark: boolean;
    isOnSidemark: boolean;
    isOnReview: boolean;
    onUpdateSideMarkStatus: typeof updateSidemarkStatusAction;
    onSaveSidemark: typeof saveSidemark;
};

export const CheckoutSidebarSidemarkForm: FunctionComponent<Props> = props => {
    const {
        viewer: viewerRef,
        transaction: transactionRef,
        sidemark,
        hasUnsavedSidemark,
        isOnSidemark,
        isOnReview,
        onUpdateSideMarkStatus,
        onSaveSidemark,
    } = props;
    const environment = useRelayEnvironment();
    const viewer = useFragment(
        graphql`
            fragment CheckoutSidebarSidemarkForm_viewer on Viewer {
                isMultiStageCheckoutOn: featureFlag(feature: "multiStageCheckout")
            }
        `,
        viewerRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutSidebarSidemarkForm_transaction on Transaction {
                serviceId
            }
        `,
        transactionRef
    );

    const context = useIntl();
    const [sidemarkState, setSidemarkState] = useState<string | null>(sidemark);
    const [editableState, setEditableState] = useState<boolean>(!sidemark);

    const onChange = (
        event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
    ): void => {
        const { value: sidemarkValue } = event.target as HTMLInputElement;
        if (sidemarkValue.trim() !== sidemarkValue && !hasUnsavedSidemark) {
            onUpdateSideMarkStatus({
                hasUnsavedSidemark: true,
            });
        } else if (sidemarkValue.trim() === sidemarkValue && hasUnsavedSidemark) {
            onUpdateSideMarkStatus({
                hasUnsavedSidemark: false,
            });
        }
        setSidemarkState(sidemarkValue);
    };

    const onSubmit = async (event: FormEvent<HTMLElement>): Promise<void> => {
        const shouldUpdate = sidemark !== sidemarkState || !!sidemarkState;
        event.preventDefault();

        if (shouldUpdate) {
            // not sure where `isMobile` is set on context
            // since this.context is IntlContext
            // Property 'isMobile' does not exist on type 'IntlShape | null'.ts(2339)
            // @ts-ignore
            const { isMobile } = context;
            if (viewer?.isMultiStageCheckoutOn) {
                try {
                    await CheckoutMutationWrapper.commit(
                        'TransactionCheckoutNote',
                        environment,
                        {
                            transactionId: transaction?.serviceId,
                            noteContent: sidemarkState,
                        },
                        isMobile
                    );
                } catch (e) {
                    logCheckoutError(e, 'sidemark');
                }
            }
            onUpdateSideMarkStatus({
                hasUnsavedSidemark: false,
            });
            onSaveSidemark((sidemarkState || '').trim(), 'save');
        }
    };

    const onBlur = (event: FocusEvent<HTMLElement>): void => {
        if (typeof sidemarkState === 'string' && !sidemarkState.trim()) {
            onSubmit(event);
        }
    };

    const onChangeEditable = (
        event: ChangeEvent<HTMLElement> | MouseEvent<HTMLElement>,
        editable: boolean
    ): void => {
        event.preventDefault();
        setEditableState(editable);
    };

    const limit = VARS.MAX_SIDEMARK_ERROR;
    const hasSidemarkError = sidemarkState && sidemarkState.length > limit;

    return (
        <form onSubmit={onSubmit}>
            <>
                {editableState ? (
                    <div className={styles.sidemarkInputs}>
                        <Input
                            readOnly={!editableState}
                            type="text"
                            size="medium"
                            value={sidemarkState || ''}
                            placeholder={context?.formatMessage(messages.placeholder)}
                            autoFocus={isOnSidemark || isOnReview}
                            dataTn="sidemark-input"
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                        <Button
                            dataTn="sidebar-button"
                            onClick={(event: MouseEvent<HTMLElement>) => {
                                onSubmit(event);
                                onChangeEditable(event, false);
                            }}
                            size="medium"
                        >
                            <span className={styles.buttonText}>{copy.save}</span>
                        </Button>
                    </div>
                ) : (
                    <CheckoutEditValue
                        dataTn="sidemark-edit-cta"
                        value={sidemarkState || ''}
                        onClick={(event: ChangeEvent<HTMLElement>) => {
                            onChangeEditable(event, true);
                        }}
                        emptyValuePlaceholder="No notes"
                    />
                )}
            </>
            {hasSidemarkError && (
                <p className={styles.error}>
                    <FormattedMessage
                        id="checkout.CheckoutSidebarSidemarkForm.error"
                        defaultMessage="We recommend limiting your notes to {limit} characters to ensure it fits on shipping labels."
                        values={{ limit }}
                    />
                </p>
            )}
        </form>
    );
};

type ConnectProps = {
    ui: {
        hasUnsavedSidemark: boolean;
    };
    transaction: {
        sidemark?: string | null;
    };
    pane: PaneState;
};

export default connect(
    ({ ui, transaction, pane }: ConnectProps) => ({
        hasUnsavedSidemark: ui.hasUnsavedSidemark,
        sidemark: transaction?.sidemark || '',
        isOnSidemark: isOnSidemarkPane(pane),
        isOnReview: isOnReviewPane(pane),
    }),
    {
        onUpdateSideMarkStatus: updateSidemarkStatusAction,
        onSaveSidemark: saveSidemark,
    }
)(CheckoutSidebarSidemarkForm);
